import { FrontendTenantConfig } from '../types';

/**
 * ISC — International Surrogacy Center. FE-relevant subset of 02's tenant contract, restated here
 * because the frontend cannot import the backend registry (`ModuleScopePlugin` + separate Yarn
 * projects). Values NOT restated (mail identity, postal address, socials, leadTracking, workflow,
 * streak) stay backend-only; 02 owns them.
 *
 * BE source: apps/backend/libs/tenant/src/tenants/isc.tenant.ts
 */
export const iscTenant: FrontendTenantConfig = {
  slug: 'isc',
  // BE source: apps/backend/libs/tenant/src/tenants/isc.tenant.ts:18
  displayName: 'International Surrogacy Center',
  phone: {
    // G1 — the FE carries the phone twice today (LightLayout.tsx:50,52); the BE registry holds a
    // THIRD format, `(619) 591-9113` (isc.tenant.ts:26) — the drift D9/R2 accept.
    display: '619-591-9113',
    tel: '16195919113',
  },
  urls: {
    // BE source: apps/backend/libs/tenant/src/tenants/isc.tenant.ts:44-48
    terms: 'https://www.internationalsurrogacycenter.com/terms-of-use/',
    privacy: 'https://www.internationalsurrogacycenter.com/privacy-policy/',
    requirements: 'https://www.internationalsurrogacycenter.com/requirements-to-become-a-surrogate/',
    contactUs: 'https://www.internationalsurrogacycenter.com/contact-us/',
    marketingBase: 'https://www.internationalsurrogacycenter.com',
  },
  pdf: {
    // §4A.5 — verified against `master`.
    creator: 'isc',
    producer: 'isc',
    applicationCoverLogoWidth: 25,
    questionnaireCoverLogoWidth: 25,
  },
  head: {
    // §4A.5 — verified against `master`'s public/index.html:7-8.
    title: 'International surrogacy center',
    description: 'Welcome to International surrogacy portal',
  },
  // SAA-61/05 D3 — authored inline, machine-diffed against the BE registry (R4) by
  // `print-frontend-workflow.ts` + `scripts/check-tenant-alignment.mjs`. Byte-identical to
  // `apps/backend/libs/tenant/src/tenants/isc.tenant.ts`, same order (§3.5).
  workflow: {
    // BE source: apps/backend/libs/tenant/src/tenants/isc.tenant.ts:64-86 (statuses)
    statuses: [
      'lead',
      'applicants',
      // SAA-112 — created by "New App"; outside `editableStatuses` (D10, locked at birth).
      'newApplicant',
      'completed',
      'potential',
      'available',
      'exploring',
      'matched',
      'legal',
      'ivfMock',
      'postTransfer',
      'obCare',
      'postDelivery',
      'delivered',
      'nurture',
      'cooling',
      'comeBackLater',
      'contactUs',
      'purged',
      'disqualified',
      'matchFellOut',
      'gcReferral',
      'doNotReachOut',
    ],
    // BE source: apps/backend/libs/tenant/src/tenants/isc.tenant.ts:88 (editableStatuses)
    editableStatuses: ['applicants', 'lead', 'nurture', 'cooling'],
    // BE source: apps/backend/libs/tenant/src/tenants/isc.tenant.ts:89-99 (referralOptions —
    // DROPDOWN set, not the accepted set, F16)
    // BE source: apps/backend/libs/tenant/src/tenants/isc.tenant.ts (intendedParentTypes)
    intendedParentTypes: [
      'alreadyHaveChildren',
      'hetero',
      'homo',
      'singleMale',
      'singleFemale',
      'singleGayMale',
      'singleGayFemale',
      'transgender',
      'domestic',
    ],
    // SAA-122 D2 — the GC lead form's six options, in AC-1 order, byte-identical on both tenants
    // (mirrors apps/backend/libs/tenant/src/tenants/isc.tenant.ts — check-tenant-alignment.mjs).
    referralOptions: ['referred', 'socialMedia', 'google', 'chatgpt', 'claude', 'other'],
  },
  // BE source: apps/backend/libs/tenant/src/tenants/isc.tenant.ts `features.matchingEnabled`
  matchingToolEnabled: true,
};
